import { Injectable } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import {
  ChargeFormService,
  Estimate,
  EstimateCharge,
  EstimateChargeDiscountType,
  EstimateData,
  EstimateFormGroup,
  EstimateInputs,
} from '@mmx/shared'

export interface BenefitsFormShape {
  deductible: FormControl<number | null>
  copayment: FormControl<number | null>
  coinsurance: FormControl<number | null>
  outOfPocketMax: FormControl<number | null>
}

export interface BenefitsRootFormShape {
  insuranceType: FormControl<EstimateInputs['insuranceType']>
  estimateType: FormControl<EstimateInputs['estimateType']>
  primary: FormGroup<BenefitsFormShape>
  hasSecondary: FormControl<boolean>
  secondary: FormGroup<BenefitsFormShape>
  secondaryInsuranceCoversCoinsurance: FormControl<boolean>
  secondaryInsuranceCoversCopayment: FormControl<boolean>
  secondaryInsuranceCoversDeductible: FormControl<boolean>
  discount: FormControl<number>
}

export type BenefitsFormGroup = FormGroup<BenefitsRootFormShape>

export interface EstimateBalanceFormPayload {
  facilityId: string | null
  templateId: string | null
  appointmentIds: string[]
  patientType: Estimate.PATIENT_TYPE
}

@Injectable()
export class EstimateBalanceFormService {
  constructor(
    private readonly fb: FormBuilder,
    private readonly chargeFormService: ChargeFormService,
  ) {}

  buildEstimateForm(): EstimateFormGroup {
    return this.fb.group({
      facilityId: this.fb.control<string | null>(null, Validators.required),
      templateId: this.fb.control<string | null>(null),
      saveTemplate: this.fb.nonNullable.control(false),
      name: this.fb.control<string | null>(null),
      episodeId: this.fb.control<string | null>(null),
      appointmentIds: this.fb.nonNullable.control<string[]>([]),
      patientType: this.fb.nonNullable.control<Estimate.PATIENT_TYPE>(
        Estimate.PATIENT_TYPE.OutPatient,
        Validators.required,
      ),
      charges: this.chargeFormService.createChargesFormArray([]),
    })
  }

  patchEstimateForm(form: EstimateFormGroup, estimate: EstimateData): void {
    form.patchValue({
      facilityId: estimate.facilityId ?? null,
      templateId: estimate.templateId ?? null,
      appointmentIds: estimate.appointmentIds ?? [],
      patientType: estimate.patientType ?? Estimate.PATIENT_TYPE.OutPatient,
    })
    if (estimate.charges) {
      this.setCharges(form, estimate.charges)
    }
  }

  setCharges(form: EstimateFormGroup, charges: EstimateCharge[]): void {
    const appointmentCount = form.controls.appointmentIds.value?.length ?? 0
    const normalized = this.chargeFormService.ensureVisitNumbers(charges, appointmentCount)
    this.chargeFormService.setChargesOnFormArray(form.controls.charges, normalized)
  }

  /**
   * Shifts charge visit numbers to follow a Course-of-Care appointment-set change. `remap` maps
   * `oldVisitNumber → newVisitNumber | null` (null drops the entry; missing keys are unchanged).
   * Mirrors the backend renumber so the form stays consistent while the change is still local.
   * Uses the low-level setter (not `setCharges`) so a charge whose only visit was removed keeps an
   * empty `visitNumbers` — `ensureVisitNumbers` would otherwise re-expand `[]` to every visit.
   */
  remapChargeVisitNumbers(form: EstimateFormGroup, remap: Map<number, number | null>): void {
    if (remap.size === 0) {
      return
    }
    const next = this.getCharges(form).map(charge => {
      if (!charge.visitNumbers) {
        return charge
      }
      const visitNumbers: number[] = []
      for (const n of charge.visitNumbers) {
        if (!remap.has(n)) {
          visitNumbers.push(n)
          continue
        }
        const mapped = remap.get(n)
        if (mapped != null) {
          visitNumbers.push(mapped)
        }
      }
      return { ...charge, visitNumbers }
    })
    this.chargeFormService.setChargesOnFormArray(form.controls.charges, next)
  }

  setFacility(form: EstimateFormGroup, facilityId: string | null): void {
    form.controls.facilityId.setValue(facilityId)
  }

  getFacility(form: EstimateFormGroup): string | null {
    return form.controls.facilityId.value
  }

  setAppointmentIds(form: EstimateFormGroup, ids: string[]): void {
    form.controls.appointmentIds.setValue([...ids])
  }

  // Single read-edge for the save payload — keep direct `form.getRawValue()` in this method only.
  getPayloadValues(form: EstimateFormGroup): EstimateBalanceFormPayload {
    const raw = form.getRawValue()
    return {
      facilityId: raw.facilityId ?? null,
      templateId: raw.templateId ?? null,
      appointmentIds: raw.appointmentIds ?? [],
      patientType: raw.patientType,
    }
  }

  getCharges(form: EstimateFormGroup): EstimateCharge[] {
    return this.chargeFormService.getChargesFromFormArray(form.controls.charges)
  }

  /**
   * `preserveVisitNumbers=false` clears `charge.visitNumbers` so stale references (e.g. after
   * a CoC toggle-OFF) don't ride along on save. Pass `true` in any visit-tracking mode (CoC or
   * unscheduled) — both depend on positive visit numbers in the persisted payload.
   */
  buildSaveChargesPayload(form: EstimateFormGroup, preserveVisitNumbers: boolean): EstimateCharge[] {
    const charges = this.getCharges(form)
    if (preserveVisitNumbers) {
      return charges
    }
    return charges.map(c => ({ ...c, visitNumbers: [] }))
  }

  getCocChargesValid(form: EstimateFormGroup): boolean {
    for (const charge of form.controls.charges.controls) {
      const control = charge.controls.visitNumbers
      if (control.hasError('invalidVisitNumbers')) {
        return false
      }
      if ((control.value?.length ?? 0) === 0) {
        return false
      }
    }
    return true
  }

  clearCharges(form: EstimateFormGroup): void {
    form.controls.charges.clear()
    form.controls.templateId.setValue(null)
  }

  addDiscountControls(
    form: EstimateFormGroup,
    chargeIndex: number,
    discountType: EstimateChargeDiscountType,
  ): void {
    this.chargeFormService.addDiscountControls(form.controls.charges, chargeIndex, discountType)
  }

  removeDiscountControls(form: EstimateFormGroup, chargeIndex: number): void {
    this.chargeFormService.removeDiscountControls(form.controls.charges, chargeIndex)
  }

  buildBenefitsForm(inputs?: EstimateInputs | null): BenefitsFormGroup {
    const safe = inputs ?? this.defaultInputs()
    return this.fb.group<BenefitsRootFormShape>({
      insuranceType: this.fb.nonNullable.control(safe.insuranceType, Validators.required),
      estimateType: this.fb.nonNullable.control(safe.estimateType, Validators.required),
      primary: this.fb.group<BenefitsFormShape>({
        deductible: this.fb.control<number | null>(safe.deductible, [Validators.min(0)]),
        copayment: this.fb.control<number | null>(safe.copayment, [Validators.min(0)]),
        coinsurance: this.fb.control<number | null>(safe.coinsurance, [Validators.min(0), Validators.max(1)]),
        outOfPocketMax: this.fb.control<number | null>(safe.outOfPocketMax),
      }),
      hasSecondary: this.fb.nonNullable.control(safe.hasSecondary ?? false),
      secondary: this.fb.group<BenefitsFormShape>({
        deductible: this.fb.control<number | null>(safe.secondaryDeductible ?? 0, [Validators.min(0)]),
        copayment: this.fb.control<number | null>(safe.secondaryCopayment ?? 0, [Validators.min(0)]),
        coinsurance: this.fb.control<number | null>(safe.secondaryCoinsurance ?? 0, [Validators.min(0), Validators.max(1)]),
        outOfPocketMax: this.fb.control<number | null>(safe.secondaryOutOfPocketMax ?? null),
      }),
      secondaryInsuranceCoversCoinsurance: this.fb.nonNullable.control(safe.secondaryInsuranceCoversCoinsurance ?? false),
      secondaryInsuranceCoversCopayment: this.fb.nonNullable.control(safe.secondaryInsuranceCoversCopayment ?? false),
      secondaryInsuranceCoversDeductible: this.fb.nonNullable.control(safe.secondaryInsuranceCoversDeductible ?? false),
      discount: this.fb.nonNullable.control(safe.discount ?? 0, [Validators.min(0)]),
    })
  }

  patchBenefitsForm(form: BenefitsFormGroup, inputs: EstimateInputs): void {
    form.patchValue({
      insuranceType: inputs.insuranceType,
      estimateType: inputs.estimateType,
      primary: {
        deductible: inputs.deductible,
        copayment: inputs.copayment,
        coinsurance: inputs.coinsurance,
        outOfPocketMax: inputs.outOfPocketMax ?? null,
      },
      hasSecondary: inputs.hasSecondary ?? false,
      secondary: {
        deductible: inputs.secondaryDeductible ?? 0,
        copayment: inputs.secondaryCopayment ?? 0,
        coinsurance: inputs.secondaryCoinsurance ?? 0,
        outOfPocketMax: inputs.secondaryOutOfPocketMax ?? null,
      },
      secondaryInsuranceCoversCoinsurance: inputs.secondaryInsuranceCoversCoinsurance ?? false,
      secondaryInsuranceCoversCopayment: inputs.secondaryInsuranceCoversCopayment ?? false,
      secondaryInsuranceCoversDeductible: inputs.secondaryInsuranceCoversDeductible ?? false,
      discount: inputs.discount ?? 0,
    }, { emitEvent: false })
  }

  benefitsFormToInputs(form: BenefitsFormGroup): EstimateInputs {
    const value = form.getRawValue()
    return {
      insuranceType: value.insuranceType,
      estimateType: value.estimateType,
      deductible: value.primary.deductible ?? 0,
      copayment: value.primary.copayment ?? 0,
      coinsurance: value.primary.coinsurance ?? 0,
      outOfPocketMax: value.primary.outOfPocketMax,
      hasSecondary: value.hasSecondary,
      secondaryDeductible: value.secondary.deductible ?? 0,
      secondaryCopayment: value.secondary.copayment ?? 0,
      secondaryCoinsurance: value.secondary.coinsurance ?? 0,
      secondaryOutOfPocketMax: value.secondary.outOfPocketMax,
      secondaryInsuranceCoversCoinsurance: value.secondaryInsuranceCoversCoinsurance,
      secondaryInsuranceCoversCopayment: value.secondaryInsuranceCoversCopayment,
      secondaryInsuranceCoversDeductible: value.secondaryInsuranceCoversDeductible,
      discount: value.discount,
    }
  }

  // Public so the flow can seed draft estimate.inputs with the same defaults this form uses.
  defaultInputs(): EstimateInputs {
    return {
      deductible: 0,
      copayment: 0,
      coinsurance: 0,
      outOfPocketMax: null,
      insuranceType: 'medical',
      estimateType: 'patientpal',
      hasSecondary: false,
      secondaryDeductible: 0,
      secondaryCopayment: 0,
      secondaryCoinsurance: 0,
      secondaryOutOfPocketMax: null,
      discount: 0,
    }
  }
}
